import { useMutation } from "@tanstack/react-query";
import { objNamesToSnakeCase } from "common/utils/helpers/cases";
import { api, type models } from "jiffy-api";
import {
  DEFAULT_TIMEOUT,
  LARGE_FILE_TIMEOUT,
  LARGE_FILE_THRESHOLD
} from "@design-lab/shared/constants";

export function useDftUploadArtwork() {
  return useMutation({
    retry: (failureCount: number, error: any) => {
      const errorData = (error as any)?.response?.data;

      // Timeout errors should be retryable
      const isTimeoutError = errorData?.error_type === "timeout_error";
      if (isTimeoutError) {
        return failureCount < 2;
      }

      // Validation errors should not be retried
      const isValidationError =
        errorData?.error_type === "backend_file_validation" || errorData?.validation_error;

      if (isValidationError) {
        return false;
      }

      return failureCount < 2;
    },
    networkMode: "always", // Always attempt the mutation even when offline, causing it to fail and trigger retries
    mutationFn: async (params: models.dtf.uploadArtwork.Request) => {
      const formData = new FormData();
      const fields = objNamesToSnakeCase(params);

      for (const [key, value] of Object.entries(fields)) {
        if (key === "file") {
          formData.append(key, params.file);
        } else {
          formData.append(key, String(value));
        }
      }

      const fileSize = params.file?.size || 0;
      const timeout = fileSize > LARGE_FILE_THRESHOLD ? LARGE_FILE_TIMEOUT : DEFAULT_TIMEOUT;

      try {
        return await api.dtf.uploadArtwork(formData, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined },
          timeout
        });
      } catch (error: any) {
        if (error?.code === "ECONNABORTED" || error?.response?.status === 504) {
          const timeoutError: any = new Error("Request timeout");
          timeoutError.response = {
            data: {
              error_type: "timeout_error",
              error:
                "Upload timed out. The file may be taking too long to process or your connection may be slow. Please try again.",
              message: "Request timeout"
            },
            status: 504
          };
          throw timeoutError;
        }
        throw error;
      }
    }
  });
}
